import React, { useMemo } from "react";
import { Modal, Table, Button } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAppSelector } from "../../../hooks/reduxHooks";

export default function TicketStatusModal({ 
  visible, 
  onClose, 
  ctaData = [] 
}) {
  const navigate = useNavigate();
  const { dateRangeValue } = useAppSelector((state) => state.dateRange);
  const startDateStr = useMemo(() =>
    dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"),
    [dateRangeValue.start_date]
  );
  const endDateStr = useMemo(() =>
    dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59).format("YYYY-MM-DD HH:mm:ss"),
    [dateRangeValue.end_date]
  );

  // Get all unique ticket types (excluding order/booking)
  const uniqueTicketTypes = [...new Set(
    ctaData
      .filter(item => 
        item && 
        item.type && 
        item.type !== "order" && 
        item.type !== "booking"
      )
      .map(item => item.type)
  )];

  // Prepare ticket breakdown data
  const ticketBreakdownData = uniqueTicketTypes
    .map((ticketType, index) => {
      // Get all items of this type and calculate totals
      const openCount = ctaData
        .filter(item => item?.type === ticketType && item?.status === "open")
        .reduce((sum, item) => sum + (item?.count || 0), 0);
      
      const resolvedCount = ctaData
        .filter(item => item?.type === ticketType && item?.status === "resolved")
        .reduce((sum, item) => sum + (item?.count || 0), 0);
      
      // Prettify type names - dynamic beautification
      const prettifyName = (type) => {
        if (!type) return 'Unknown';
        return type
          .split(/[_\-\s]+/) // Split by underscore, dash, or space
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      };

      return {
        key: index,
        type: prettifyName(ticketType),
        originalType: ticketType, // Keep original type for navigation
        pending: openCount,
        resolved: resolvedCount,
        total: openCount + resolvedCount
      };
    });

  // Calculate column totals
  const columnTotals = {
    pending: ticketBreakdownData.reduce((sum, row) => sum + row.pending, 0),
    resolved: ticketBreakdownData.reduce((sum, row) => sum + row.resolved, 0),
    total: ticketBreakdownData.reduce((sum, row) => sum + row.total, 0)
  };

  // Add totals row to the data
  const dataWithTotals = [
    ...ticketBreakdownData,
    {
      key: 'totals',
      type: 'Total',
      pending: columnTotals.pending,
      resolved: columnTotals.resolved,
      total: columnTotals.total
    }
  ];

  // Handle navigation for pending tickets
  const handlePendingClick = (record) => {
    // Don't navigate if it's the totals row
    if (record.key === 'totals') {
      return;
    }
    
    onClose();
    // For specific type, navigate with type and status filter (even if count is 0)
    const originalType = record.originalType || record.type.toLowerCase().replace(/ /g, '_');
    const params = new URLSearchParams({ type: originalType, status: 'open', start_date: startDateStr, end_date: endDateStr });
    navigate(`/cta?${params.toString()}`);
  };

  // Handle navigation for resolved tickets
  const handleResolvedClick = (record) => {
    // Don't navigate if it's the totals row
    if (record.key === 'totals') {
      return;
    }
    
    onClose();
    // For specific type, navigate with type and status filter (even if count is 0)
    const originalType = record.originalType || record.type.toLowerCase().replace(/ /g, '_');
    const params = new URLSearchParams({ type: originalType, status: 'resolved', start_date: startDateStr, end_date: endDateStr });
    navigate(`/cta?${params.toString()}`);
  };

  // Handle navigation for total tickets
  const handleTotalClick = (record) => {
    // Don't navigate if it's the totals row
    if (record.key === 'totals') {
      return;
    }
    
    onClose();
    // For specific type, navigate with only type filter (even if count is 0)
    const originalType = record.originalType || record.type.toLowerCase().replace(/ /g, '_');
    const params = new URLSearchParams({ type: originalType, start_date: startDateStr, end_date: endDateStr, status: '' });
    navigate(`/cta?${params.toString()}`);
  };

  const ticketColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => {
        if (record.key === 'totals') {
          return <strong style={{ color: '#000' }}>{text}</strong>;
        }
        return text;
      }
    },
    {
      title: 'Pending',
      dataIndex: 'pending',
      key: 'pending',
      onCell: (record) => ({
        onClick: () => handlePendingClick(record),
        style: {
          cursor: record.key === 'totals' ? 'default' : 'pointer',
          padding: '12px 16px',
        },
        className: record.key === 'totals' ? 'non-clickable-cell pending-cell' : 'clickable-cell pending-cell',
      }),
      render: (count, record) => {
        const textColor = record.key === 'totals' ? '#000' : '#ff4d4f';
        const fontWeight = record.key === 'totals' ? 'bold' : 'normal';
        
        return (
          <span style={{ 
            // color: textColor,
            fontWeight: fontWeight,
            display: 'block',
            width: '100%',
            height: '100%'
          }}>
            {count}
          </span>
        );
      }
    },
    {
      title: 'Resolved',
      dataIndex: 'resolved',
      key: 'resolved',
      onCell: (record) => ({
        onClick: () => handleResolvedClick(record),
        style: {
          cursor: record.key === 'totals' ? 'default' : 'pointer',
          padding: '12px 16px',
        },
        className: record.key === 'totals' ? 'non-clickable-cell resolved-cell' : 'clickable-cell resolved-cell',
      }),
      render: (count, record) => {
        const textColor = record.key === 'totals' ? '#000' : '#52c41a';
        const fontWeight = record.key === 'totals' ? 'bold' : 'normal';
        
        return (
          <span style={{ 
            // color: textColor,
            fontWeight: fontWeight,
            display: 'block',
            width: '100%',
            height: '100%'
          }}>
            {count}
          </span>
        );
      }
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      onCell: (record) => ({
        onClick: () => handleTotalClick(record),
        style: {
          cursor: record.key === 'totals' ? 'default' : 'pointer',
          padding: '12px 16px',
        },
        className: record.key === 'totals' ? 'non-clickable-cell total-cell' : 'clickable-cell total-cell',
      }),
      render: (count, record) => {
        return (
          <strong style={{ 
            color: '#000',
            display: 'block',
            width: '100%',
            height: '100%'
          }}>
            {count}
          </strong>
        );
      }
    },
  ];

  return (
    <Modal
      title="Ticket Breakdown Details"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={600}
    >
      <Table
        dataSource={dataWithTotals}
        columns={ticketColumns}
        pagination={false}
        size="middle"
        style={{ marginTop: 16 }}
        rowClassName={(record) => {
          return record.key === 'totals' ? 'totals-row' : '';
        }}
      />
      <style jsx>{`
        tr:hover > td {
          background: transparent !important;
        }
        .clickable-cell:hover {
          background: #f1f1f2 !important;
        }

        .non-clickable-cell:hover {
          background: transparent !important;
        }
      `}</style>
    </Modal>
  );
}